import { observer } from 'mobx-react-lite'
import { useNavigate } from 'react-router-dom'
import Button from '@/components/common/Button'
import { workflowStore } from '@/stores/WorkflowStore'
import {
  ArrowPathIcon,
  TrashIcon,
  SparklesIcon,
  PlayIcon,
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'

interface WorkflowToolbarProps {
  onClear: () => void
  onAutoLayout: () => void
  canSave: boolean
}

const WorkflowToolbar = observer(({ onClear, onAutoLayout, canSave }: WorkflowToolbarProps) => {
  const navigate = useNavigate()

  const handleSave = async () => {
    try {
      await workflowStore.saveWorkflow()
      toast.success('Workflow saved successfully!')
    } catch (error) {
      toast.error('Failed to save workflow')
    }
  }

  const handleExecute = async () => {
    if (!workflowStore.selectedWorkflow) {
      toast.error('Please save the workflow first')
      return
    }

    // Check if there are unsaved changes
    if (workflowStore.isDirty) {
      const confirm = window.confirm(
        'You have unsaved changes. Would you like to save before executing?'
      )
      if (confirm) {
        try {
          await workflowStore.saveWorkflow()
          toast.success('Workflow saved!')
        } catch (error) {
          toast.error('Failed to save workflow')
          return
        }
      }
    }

    // Navigate to the workflow execution page
    navigate(`/workflows/${workflowStore.selectedWorkflow.id}/execute`)
  }

  return (
    <div className="bg-white border-b border-gray-200 px-4 py-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold text-gray-900">
            {workflowStore.selectedWorkflow?.name || 'Untitled Workflow'}
          </h2>
          {workflowStore.isDirty && (
            <span className="text-xs text-orange-600 font-medium">• Unsaved changes</span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={onAutoLayout}
            title="Auto-layout nodes"
          >
            <SparklesIcon className="w-4 h-4 mr-1" />
            Auto Layout
          </Button>

          <Button
            variant="ghost"
            size="sm"
            onClick={onClear}
            title="Clear canvas"
          >
            <TrashIcon className="w-4 h-4 mr-1" />
            Clear
          </Button>

          <div className="border-l border-gray-300 h-6 mx-2" />

          <Button
            variant="secondary"
            size="sm"
            onClick={handleExecute}
            disabled={workflowStore.nodes.length === 0}
          >
            <PlayIcon className="w-4 h-4 mr-1" />
            Test Run
          </Button>

          <Button
            variant="primary"
            size="sm"
            onClick={handleSave}
            disabled={!canSave}
            isLoading={workflowStore.isLoading}
          >
            <ArrowPathIcon className="w-4 h-4 mr-1" />
            Save
          </Button>
        </div>
      </div>

      <div className="mt-2 flex items-center gap-4 text-xs text-gray-600">
        <span>{workflowStore.nodes.length} nodes</span>
        <span>{workflowStore.edges.length} connections</span>
        {workflowStore.selectedWorkflow && (
          <span className="text-gray-400">
            ID: {workflowStore.selectedWorkflow.id.slice(0, 8)}
          </span>
        )}
      </div>
    </div>
  )
})

export default WorkflowToolbar
